import React from 'react';
import { Shield, Mail, Lock, FileText, Server } from 'lucide-react';

/**
 * Privacy Policy for ELR Industries ERP / Android App.
 * Published at /privacy and /privacy-policy — linked from Google Play Console.
 * All app-related communications: elrinfra@gmail.com
 */
export default function PrivacyPolicy() {
  const lastUpdated = '27 August 2026';
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#1F7A6E] flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.25em] text-[#1F7A6E]">ELR Industries Pvt Ltd</div>
              <h1 className="font-display font-bold text-base text-slate-900">Privacy Policy</h1>
            </div>
          </div>
          <a href="https://erp.elrindia.com" className="text-xs text-[#1F7A6E] font-display font-semibold hover:underline">erp.elrindia.com</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8" data-testid="privacy-policy-page">
        <p className="text-xs text-slate-500 mb-6">Last updated: {lastUpdated}</p>

        <Section icon={FileText} title="1. About This Policy">
          <p>This Privacy Policy describes how <strong>ELR Industries Pvt Ltd</strong> ("ELR Industries", "we", "us", "our") collects, uses, and protects information when employees, contractors, vendors, site staff, and other authorised users ("User", "you") access the <strong>ELR Industries ERP</strong> application — available as a web app at <a className="text-[#1F7A6E]" href="https://erp.elrindia.com">https://erp.elrindia.com</a> and as an Android app on the Google Play Store.</p>
          <p>By using the App, you acknowledge and accept this Policy. Access is granted under your employment, contractor, or vendor relationship with ELR Industries and may be revoked at any time.</p>
        </Section>

        <Section icon={Server} title="2. Information We Collect">
          <p>The App collects only the data needed to deliver business workflows:</p>
          <ul>
            <li><strong>Account &amp; profile data</strong> — name, work email, phone, employee ID, department, designation, reporting manager, and role.</li>
            <li><strong>Operational data</strong> — production entries, dispatch challans, lab tests, daily progress reports, procurement records, invoices, attendance, HSE incidents, tasks, and approvals you create or interact with.</li>
            <li><strong>Device &amp; location data</strong> — GPS coordinates and timestamp when you punch in/out or capture a site visit; device model, OS version, and IP address for security auditing.</li>
            <li><strong>Camera &amp; media</strong> — photos and videos you explicitly capture for DPR, HSE incidents, or face-verified attendance. Face descriptors used for verification are stored as encoded vectors, not raw images.</li>
            <li><strong>Files you upload</strong> — drawings, documents, invoices, and other attachments stored in our project storage.</li>
            <li><strong>Authentication tokens</strong> — JWT session tokens kept securely on your device for the duration of your active session; logging out invalidates the token server-side.</li>
            <li><strong>Diagnostic &amp; audit logs</strong> — anonymised request logs (endpoint, status, latency) for security monitoring; audit trails of data creation and modification.</li>
          </ul>
          <p>The App <strong>does not</strong> collect contacts, SMS, call logs, browsing history outside the App, advertising identifiers, or biometric data from your device's hardware sensors.</p>
        </Section>

        <Section icon={Shield} title="3. How We Use Your Data">
          <p>All data is used strictly for legitimate business operations:</p>
          <ul>
            <li>Running ERP workflows: project execution, procurement, billing, HR, attendance, quality, and HSE.</li>
            <li>Calculating cost performance, metrics, and management reports.</li>
            <li>Enforcing access control, audit trails, and statutory compliance.</li>
            <li>Sending in-app notifications, email digests, and approval prompts.</li>
            <li>Investigating security incidents and preventing misuse.</li>
          </ul>
          <p>We do <strong>not</strong> sell, rent, monetise, or share your data with advertisers or unaffiliated third parties.</p>
        </Section>

        <Section icon={Lock} title="4. Data Storage &amp; Security">
          <ul>
            <li>Data is stored on infrastructure managed by ELR Industries and its hosting providers, with encryption in transit (HTTPS / TLS) and at rest.</li>
            <li>Passwords are stored as bcrypt hashes — never in plain text.</li>
            <li>Session tokens are JWT-based with server-side revocation on logout.</li>
            <li>Role-based access control ensures users only see data relevant to their role.</li>
            <li>Security findings from periodic audits are tracked and remediated.</li>
          </ul>
        </Section>

        <Section icon={FileText} title="5. Data Retention">
          <p>Operational records are retained for the duration of your engagement with ELR Industries and for periods mandated by Indian statutory requirements (Companies Act, Income Tax Act, GST, EPF, labour laws). Project and financial records are typically retained for at least 8 years post-completion.</p>
          <p>Upon termination of engagement, your account is deactivated. Personal data may be anonymised or deleted upon written request to <strong>elrinfra@gmail.com</strong>, subject to legal-retention obligations.</p>
        </Section>

        <Section icon={Shield} title="6. Android App Permissions">
          <p>The App requests permissions only when necessary and only when you initiate the corresponding action:</p>
          <ul>
            <li><strong>Camera</strong> — to capture DPR/HSE photos and for face-verified attendance.</li>
            <li><strong>Location (fine)</strong> — to geo-stamp punch-in/out and site visit reports.</li>
            <li><strong>Storage / Photos</strong> — to upload attachments you choose.</li>
            <li><strong>Internet &amp; Network state</strong> — to reach the ERP backend.</li>
            <li><strong>Notifications (Android 13+)</strong> — for in-app pings on tasks, approvals, and alerts.</li>
          </ul>
          <p>All permissions can be revoked from <em>Android Settings &rarr; Apps &rarr; ELR Industries ERP &rarr; Permissions</em> at any time.</p>
        </Section>

        <Section icon={FileText} title="7. Children's Privacy">
          <p>The App is intended for adult employees and authorised personnel only (18+). We do not knowingly collect personal data from minors.</p>
        </Section>

        <Section icon={FileText} title="8. Changes to This Policy">
          <p>We may update this Policy from time to time. Material changes will be notified inside the App. Continued use after the effective date constitutes acceptance.</p>
        </Section>

        <Section icon={Mail} title="9. Contact Us">
          <p>For privacy questions, data-access or deletion requests, or any app-related communication:</p>
          <div className="bg-white border border-slate-200 rounded-sm p-4 my-3 text-sm">
            <div className="font-display font-bold text-slate-900">ELR Industries Pvt Ltd</div>
            <div className="text-slate-600 mt-1">App &amp; Privacy Enquiries</div>
            <div className="text-slate-600 mt-2">Email: <a className="text-[#1F7A6E] font-semibold" href="mailto:elrinfra@gmail.com">elrinfra@gmail.com</a></div>
            <div className="text-slate-600">Website: <a className="text-[#1F7A6E] font-semibold" href="https://erp.elrindia.com" target="_blank" rel="noreferrer">https://erp.elrindia.com</a></div>
          </div>
        </Section>

        <footer className="border-t border-slate-200 mt-10 pt-6 pb-12 text-xs text-slate-500 flex flex-wrap gap-4 items-center justify-between">
          <span>© {new Date().getFullYear()} ELR Industries Pvt Ltd. All rights reserved. Governed by the laws of India; disputes subject to courts in Pune, Maharashtra.</span>
          <a href="/terms" className="text-[#1F7A6E] hover:underline font-medium">Terms &amp; Conditions &rarr;</a>
        </footer>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-8">
      <h2 className="flex items-center gap-2 font-display font-bold text-base text-slate-900 mb-3 mt-6">
        <Icon size={15} className="text-[#1F7A6E]" />{title}
      </h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
        {children}
      </div>
    </section>
  );
}
